import { type ComputedRef, type Ref, computed, onScopeDispose, ref, watch } from 'vue'

import type { Subscription } from '@/lib/firebase/subscribe'
import type { SetupScope } from '@/repositories/setup-docs'
import { useAuthStore } from '@/stores/auth'
import { failed, isLoaded, loading, type LoadState } from '@/types/load-state'
import {
  RUN_STALE_AFTER_MS,
  type SetupRun,
  isRunLive,
  runStaleAt,
  withRunTimeout,
} from '@/types/setup-run'

export function currentSetupScope(): SetupScope | null {
  const auth = useAuthStore()
  const workspaceId = auth.claims?.workspaceId
  const brandId = auth.claims?.brandId
  if (!workspaceId || !brandId) return null
  return { workspaceId, brandId }
}

export interface ScopedSubscription<T> {
  readonly state: Ref<LoadState<T>>
  open(scope: SetupScope): void
  close(): void
  fail(message: string): void
}

/** A brand-scoped listener a store opens and closes; call it inside a store's setup. */
export function useScopedSubscription<T>(
  subscribe: (scope: SetupScope) => Subscription<T>,
): ScopedSubscription<T> {
  const state = ref<LoadState<T>>(loading()) as Ref<LoadState<T>>
  let sub: Subscription<T> | null = null
  let stopWatch: (() => void) | null = null

  function open(scope: SetupScope): void {
    if (sub) return
    state.value = loading()
    sub = subscribe(scope)
    stopWatch = watch(
      sub.state,
      (next) => {
        state.value = next
      },
      { immediate: true },
    )
  }

  function close(): void {
    sub?.stop()
    sub = null
    stopWatch?.()
    stopWatch = null
    state.value = loading()
  }

  function fail(message: string): void {
    state.value = failed(message)
  }

  return { state, open, close, fail }
}

export interface LatestRun<R extends SetupRun> extends ScopedSubscription<R | null> {
  readonly run: ComputedRef<R | null>
  readonly isLive: ComputedRef<boolean>
}

/** The latest run of a step: a brand-scoped listener plus a timer that flips a quiet running run to timed out; call it inside a store's setup. */
export function useLatestRun<R extends SetupRun>(
  subscribe: (scope: SetupScope) => Subscription<R | null>,
  staleAfterMs: number = RUN_STALE_AFTER_MS,
): LatestRun<R> {
  const subscription = useScopedSubscription(subscribe)
  const now = ref(Date.now())
  let staleTimer: ReturnType<typeof setTimeout> | null = null

  const stored = computed<R | null>(() =>
    isLoaded(subscription.state.value) ? subscription.state.value.value : null,
  )

  function clearStaleTimer(): void {
    if (staleTimer !== null) clearTimeout(staleTimer)
    staleTimer = null
  }

  watch(
    stored,
    (latest) => {
      clearStaleTimer()
      now.value = Date.now()
      const staleAt = latest ? runStaleAt(latest, staleAfterMs) : null
      if (staleAt === null || staleAt <= now.value) return
      staleTimer = setTimeout(() => {
        staleTimer = null
        now.value = Date.now()
      }, staleAt - now.value)
    },
    { immediate: true },
  )

  onScopeDispose(clearStaleTimer)

  const run = computed<R | null>(() =>
    stored.value ? withRunTimeout(stored.value, now.value, staleAfterMs) : null,
  )
  const isLive = computed(() => isRunLive(run.value))

  return { ...subscription, run, isLive }
}
